import {
  CRM_LEGACY_SELECTED_BOARD_STORAGE_KEYS,
  CRM_SELECTED_BOARD_STORAGE_KEY,
  DEFAULT_BOARD_COLUMNS,
} from './constants';

function toNumberOrFallback(value, fallback) {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export function sortBoardsByName(boards = []) {
  return [...boards].sort((left, right) =>
    String(left?.board_name || '').localeCompare(String(right?.board_name || ''))
  );
}

export function sortColumnsByPosition(columns = []) {
  return [...columns].sort((left, right) => {
    const leftPosition = toNumberOrFallback(left?.position, Number.MAX_SAFE_INTEGER);
    const rightPosition = toNumberOrFallback(right?.position, Number.MAX_SAFE_INTEGER);

    if (leftPosition === rightPosition) {
      return toNumberOrFallback(left?.column_id, Number.MAX_SAFE_INTEGER)
        - toNumberOrFallback(right?.column_id, Number.MAX_SAFE_INTEGER);
    }

    return leftPosition - rightPosition;
  });
}

function normalizeInvestor(investor = {}) {
  return {
    ...investor,
    investor_id: investor?.investor_id ?? investor?._id ?? investor?.id,
    note: investor?.note || '',
    value: investor?.value ?? 0,
  };
}

export function normalizeBoard(board = {}) {
  const fallbackColumns = board?.columns?.length ? board.columns : DEFAULT_BOARD_COLUMNS;

  return {
    ...board,
    columns: sortColumnsByPosition(
      fallbackColumns.map((column, index) => ({
        ...column,
        position: column?.position ?? index + 1,
        investors: Array.isArray(column?.investors)
          ? column.investors.map(normalizeInvestor)
          : [],
      }))
    ),
  };
}

export function normalizeBoards(boards = []) {
  return sortBoardsByName((boards || []).map(normalizeBoard));
}

export function getBoardById(boards = [], boardId) {
  if (boardId == null) {
    return null;
  }

  return boards.find((board) => String(board?.board_id) === String(boardId)) || null;
}

export function getAllBoardInvestorIds(boards = []) {
  return [...new Set(
    boards.flatMap((board) =>
      (board?.columns || []).flatMap((column) =>
        (column?.investors || []).map((investor) => String(investor?.investor_id || '')).filter(Boolean)
      )
    )
  )];
}

export function mergeBoardInvestorDetails(boards = [], investorDetailsMap = {}) {
  return boards.map((board) => ({
    ...board,
    columns: (board?.columns || []).map((column) => ({
      ...column,
      investors: (column?.investors || []).map((investor) => ({
        ...investor,
        details: investorDetailsMap[investor.investor_id] || investor.details || null,
      })),
    })),
  }));
}

export function getBoardResearchInvestorsCount(board) {
  const researchColumn = (board?.columns || []).find(
    (column) => String(column?.column_id) === '1'
  );

  return Array.isArray(researchColumn?.investors) ? researchColumn.investors.length : 0;
}

export function getBoardTotalInvestorValue(board) {
  return (board?.columns || []).reduce(
    (boardTotal, column) =>
      boardTotal + (column?.investors || []).reduce(
        (columnTotal, investor) => columnTotal + (Number(investor?.value) || 0),
        0
      ),
    0
  );
}

export function findInvestorColumnId(board, investorId) {
  const matchingColumn = (board?.columns || []).find((column) =>
    (column?.investors || []).some(
      (investor) => String(investor?.investor_id) === String(investorId)
    )
  );

  return matchingColumn?.column_id ?? null;
}

export function isInvestorInBoard(board, investorId) {
  return findInvestorColumnId(board, investorId) != null;
}

export function moveInvestorBetweenColumns(board, sourceColumnId, destinationColumnId, investorId, destinationIndex) {
  const sourceColumn = (board?.columns || []).find(
    (column) => String(column?.column_id) === String(sourceColumnId)
  );
  const destinationColumn = (board?.columns || []).find(
    (column) => String(column?.column_id) === String(destinationColumnId)
  );

  if (!sourceColumn || !destinationColumn) {
    return board;
  }

  if (String(sourceColumnId) === String(destinationColumnId)) {
    const reorderedInvestors = [...(sourceColumn.investors || [])];
    const investorIndex = reorderedInvestors.findIndex(
      (investor) => String(investor?.investor_id) === String(investorId)
    );

    if (investorIndex === -1) {
      return board;
    }

    const [movedInvestor] = reorderedInvestors.splice(investorIndex, 1);
    reorderedInvestors.splice(destinationIndex, 0, movedInvestor);

    return {
      ...board,
      columns: board.columns.map((column) =>
        String(column?.column_id) === String(sourceColumnId)
          ? { ...column, investors: reorderedInvestors }
          : column
      ),
    };
  }

  const movedInvestor = (sourceColumn.investors || []).find(
    (investor) => String(investor?.investor_id) === String(investorId)
  );

  if (!movedInvestor) {
    return board;
  }

  const sourceInvestors = (sourceColumn.investors || []).filter(
    (investor) => String(investor?.investor_id) !== String(investorId)
  );
  const destinationInvestors = [...(destinationColumn.investors || [])];
  destinationInvestors.splice(destinationIndex, 0, movedInvestor);

  return {
    ...board,
    columns: board.columns.map((column) => {
      if (String(column?.column_id) === String(sourceColumnId)) {
        return { ...column, investors: sourceInvestors };
      }

      if (String(column?.column_id) === String(destinationColumnId)) {
        return { ...column, investors: destinationInvestors };
      }

      return column;
    }),
  };
}

export function readStoredSelectedBoardId() {
  if (typeof window === 'undefined') {
    return null;
  }

  const storageKeys = [
    CRM_SELECTED_BOARD_STORAGE_KEY,
    ...CRM_LEGACY_SELECTED_BOARD_STORAGE_KEYS,
  ];

  for (const key of storageKeys) {
    const value = window.localStorage.getItem(key);
    const parsed = toNumberOrFallback(value, null);

    if (parsed != null) {
      return parsed;
    }
  }

  return null;
}

export function persistSelectedBoardId(boardId) {
  if (typeof window === 'undefined') {
    return;
  }

  const storageKeys = [
    CRM_SELECTED_BOARD_STORAGE_KEY,
    ...CRM_LEGACY_SELECTED_BOARD_STORAGE_KEYS,
  ];

  storageKeys.forEach((key) => {
    if (boardId == null) {
      window.localStorage.removeItem(key);
      return;
    }

    window.localStorage.setItem(key, String(boardId));
  });
}
